import { GlobalStateController } from "bai-react-global-state";
import type { User } from "firebase/auth";
import _ from "lodash";
import React from "react";
import type Stripe from "stripe";

import { presentations as presentationsApi, workspaces as workspacesApi } from "apis/callables";
import type { GetWorkspaceStripeDataResponse, UpgradeWorkspacePlanRequest } from "apis/types";
import { PusherEventType } from "common/constants";
import { IUserStorageModel, IWorkspaceDocumentChangedEvent, WorkspacePlanTier } from "common/interfaces";
import stripeProducts, { RecurringPlanDefinition } from "common/stripeProducts";
import { getSequentialRunner, SequentialRunner } from "common/utils/runSequentially";
import WorkspaceController from "js/controllers/WorkspaceController";
import Api from "js/core/api";
import getLogger, { LogGroup } from "js/core/logger";
import pusher, { ExtendedChannel } from "js/core/services/pusher";
import { getStripe, handleStripeErrors } from "js/core/services/stripe";
import { trackActivity } from "js/core/utilities/utilities";
import { auth } from "js/firebase/auth";
import { app } from "js/namespaces";
import Spinner from "js/react/components/Spinner";
import { ShowMessageDialog, ShowWarningDialog } from "../react/components/Dialogs/BaseDialog";

const logger = getLogger(LogGroup.BILLING);

export interface BillingControllerState {
    initialized: boolean;
    initializeError: Error | null;
    workspaceId: string;
    stripeData: GetWorkspaceStripeDataResponse;
}

const initialState: BillingControllerState = {
    initialized: false,
    initializeError: null,
    workspaceId: null,
    stripeData: {
        customer: null,
        subscription: null,
        upcomingInvoice: null,
        invoices: {},
        paymentMethods: {}
    }
};

class BillingController extends GlobalStateController<BillingControllerState> {
    private _firebaseUser: User;
    private _pusherChannel: ExtendedChannel;
    private _pusherChannelUnbind: () => void
    private _runSequentially: SequentialRunner
    private _userModel: IUserStorageModel;

    public get initialized() {
        return this._state.initialized;
    }

    constructor() {
        super(_.cloneDeep(initialState));
        this._runSequentially = getSequentialRunner();
    }

    public async initialize(workspaceId: string, firebaseUser: User, userModel: IUserStorageModel) {
        await this._runSequentially(async () => {
            logger.info("[BillingController] initialize()", { workspaceId, uid: firebaseUser.uid });

            await this._reset(false);

            try {
                this._firebaseUser = firebaseUser;
                this._userModel = userModel;

                const stripeData = await workspacesApi.getWorkspaceStripeData({ workspaceId });

                this._pusherChannel = await pusher.subscribe(`private-legacy-workspace-${workspaceId === "personal" ? firebaseUser.uid : workspaceId}`);
                this._pusherChannelUnbind = this._pusherChannel.bindChunked(PusherEventType.DATA_RECORD_UPDATED, this._onPusherEvent);

                await this._updateState({
                    initialized: true,
                    stripeData,
                    workspaceId
                });
            } catch (err) {
                logger.error(err, "[BillingController] initialize() failed");

                await this._updateState({
                    ..._.cloneDeep(initialState),
                    initialized: false,
                    initializeError: err
                });
            }
        });
    }

    get usedSeatCount() {
        return WorkspaceController.usedSeatCount;
    }

    get paidSeatCount() {
        return this._state.stripeData.subscription ? this._state.stripeData.subscription.items.data[0].quantity : 0;
    }

    get availableSeatCount() {
        return Math.max(this.paidSeatCount - this.usedSeatCount, 0);
    }

    get hasPaymentMethod() {
        return Object.keys(this._state.stripeData.paymentMethods).length > 0;
    }

    get canChangeSubscriptionQuantity() {
        return this.hasPaymentMethod && !WorkspaceController.plan.isManaged;
    }

    get workspaceId() {
        return this._state.workspaceId;
    }

    public async reset() {
        return this._reset();
    }

    public async reloadStripeData() {
        return this._runSequentially(async () => {
            const stripeData = await workspacesApi.getWorkspaceStripeData({ workspaceId: this._state.workspaceId });
            await this._updateState({ stripeData });
        });
    }

    public async cancelSubscription() {
        return this._runSequentially(async () => {
            const subscription = await workspacesApi.cancelSubscription({ workspaceId: this._state.workspaceId });
            await this._updateState(state => ({ ...state, stripeData: { ...state.stripeData, subscription } }));
        });
    }

    public async reactivateSubscription() {
        return this._runSequentially(async () => {
            const subscription = await workspacesApi.reactivateSubscription({ workspaceId: this._state.workspaceId });
            await this._updateState(state => ({ ...state, stripeData: { ...state.stripeData, subscription } }));
        });
    }

    public async switchSubscriptionBillingInterval(billingInterval: "month" | "year") {
        return this._runSequentially(async () => {
            const subscription = await workspacesApi.switchSubscriptionBillingInterval({ workspaceId: this._state.workspaceId, billingInterval });
            await this._updateState(state => ({ ...state, stripeData: { ...state.stripeData, subscription } }));
        });
    }

    public async updatePaymentMethod(paymentMethodId: string) {
        return this._runSequentially(async () => {
            const { subscription, paymentMethods } = await workspacesApi.updatePaymentMethod({ workspaceId: this._state.workspaceId, paymentMethodId });
            await this._updateState(state => ({ ...state, stripeData: { ...state.stripeData, subscription, paymentMethods } }));
        });
    }

    public async payLatestInvoiceOnPastDueSubscription() {
        return this._runSequentially(async () => {
            const response = await workspacesApi.payLatestInvoiceOnPastDueSubscription({ workspaceId: this._state.workspaceId });

            if (response.actionRequired) {
                const stripe = await getStripe();
                const { error } = await stripe.confirmCardPayment(response.paymentIntent.client_secret, {});
                if (error) {
                    throw new Error(error.message);
                }
            }

            const stripeData = await workspacesApi.getWorkspaceStripeData({ workspaceId: this._state.workspaceId });
            await this._updateState(state => ({ ...state, stripeData }));
        });
    }

    public async endTrialOnSubscription() {
        return this._runSequentially(async () => {
            const { subscription, upcomingInvoice, invoices } = await workspacesApi.endTrialOnSubscription({ workspaceId: this._state.workspaceId });
            await this._updateState(state => ({ ...state, stripeData: { ...state.stripeData, subscription, upcomingInvoice, invoices } }));
        });
    }

    public async applyPromotionCodeToSubscription(promotionCode: string) {
        return this._runSequentially(async () => {
            const { subscription, upcomingInvoice } = await workspacesApi.applyPromotionCodeToSubscription({ workspaceId: this._state.workspaceId, promotionCode });
            await this._updateState(state => ({ ...state, stripeData: { ...state.stripeData, subscription, upcomingInvoice } }));
        });
    }

    public async updateBillingDetails(update: { address?: Stripe.Address, name?: string, email?: string, taxId?: Pick<Stripe.TaxId, "type" | "value"> }) {
        return this._runSequentially(async () => {
            const customer = await workspacesApi.updateBillingDetails({ workspaceId: this._state.workspaceId, ...update });
            await this._updateState(state => ({ ...state, stripeData: { ...state.stripeData, customer } }));
        });
    }

    public async updateSubscriptionQuantity(quantity: number) {
        return this._runSequentially(async () => {
            const currentQuantity = this.paidSeatCount;

            const response = await workspacesApi.updateSubscriptionQuantity({ workspaceId: this._state.workspaceId, quantity });

            if (response.actionRequired === false) {
                await this._updateState(state => ({ ...state, stripeData: { ...state.stripeData, subscription: response.subscription } }));
            }

            if (response.actionRequired === true) {
                const stripe = await getStripe();
                const { paymentIntent, invoiceId } = response;
                const { error } = await stripe.confirmCardPayment(paymentIntent.client_secret, { payment_method: paymentIntent.payment_method });
                if (error) {
                    throw new Error(error.message);
                }
                const subscription = await workspacesApi.finalizeUpdateSubscriptionQuantity({ workspaceId: this._state.workspaceId, quantity, invoiceId });
                await this._updateState(state => ({ ...state, stripeData: { ...state.stripeData, subscription } }));
            }

            //// Legacy analytics ////
            if (quantity > currentQuantity) {
                const billingProps = {
                    workspace_id: this._state.workspaceId,
                    seats_added: quantity - currentQuantity,
                    type: "expansion",
                    total_seats: quantity
                };

                trackActivity("Organization", "BillingIntentComplete", null, null, billingProps, { audit: true });
                await Api.klaviyoTrack.post({
                    eventName: "Organization:BillingIntentComplete",
                    billingProps,
                });
            }
            ////
        });
    }

    public withInitializedState<T extends React.ComponentType<any>>(Component: T, PreloadComponent: React.ComponentType<any> = Spinner, ErrorComponent: React.ComponentType<any> = () => null) {
        function Wrapper(props: React.ComponentProps<T>) {
            const { initialized, initializeError } = props;

            if (initializeError) {
                return <ErrorComponent {...props} />;
            }

            if (!initialized) {
                return <PreloadComponent {...props} />;
            }

            return <Component {...props} />;
        }

        return this.withState(Wrapper);
    }

    private _onPusherEvent = (documentChangeEvents: IWorkspaceDocumentChangedEvent[]) => {
        this._runSequentially(async () => {
            for (const event of documentChangeEvents) {
                if (event.documentType === "WorkspacePlan") {
                    try {
                        const stripeData = await workspacesApi.getWorkspaceStripeData({ workspaceId: this._state.workspaceId });
                        this._updateState({ stripeData });
                    } catch (err) {
                        logger.error(err, "[BillingController] _onPusherEvent() failed");
                    }
                }
            }
        });
    }

    private _reset(runSequentially = true) {
        const reset = async () => {
            logger.info("[BillingController] reset()");

            this._firebaseUser = null;
            this._userModel = null;

            if (this._pusherChannel) {
                this._pusherChannelUnbind();
                if (!this._pusherChannel.isInUse) {
                    pusher.unsubscribe(this._pusherChannel.name);
                }
                this._pusherChannel = null;
                this._pusherChannelUnbind = null;
            }

            await this._updateState(_.cloneDeep(initialState));
        };

        if (runSequentially) {
            return this._runSequentially(reset);
        }

        return reset();
    }
}

const billingController = new BillingController();

// for lazy import and debug
app.billingController = billingController;

export default billingController;

/**
 * Upgrades a workspace plan
 *
 * Stateless, but will force refresh on the controller if initialized
 */
export async function upgradeWorkspacePlan(options: UpgradeWorkspacePlanRequest & { analyticsProps?: { [key: string]: any; } }) {
    const { workspaceId } = options;

    let { state, subscription } = await workspacesApi.upgradeWorkspacePlan(options);

    const stripe = await getStripe();

    if (state.type === "incomplete") {
        try {
            if (state.actionType === "confirm_payment") {
                await handleStripeErrors(() => stripe.confirmCardPayment(state.paymentIntent.client_secret, {
                    payment_method: state.paymentIntent.payment_method
                }));
            }

            // Finalize is only called if the subscription require additional actions
            ({ state, subscription } = await workspacesApi.finalizeUpgradeWorkspacePlan({ workspaceId }));
        } catch (err) {
            logger.error(err, "[BillingController] upgradeWorkspacePlan() failed to confirm payment/setup, will abort upgrade");

            // Abort will remove the subscription so we don't have to wait for it to go into incomplete_expired state itself
            await workspacesApi.abortUpgradeWorkspacePlan({ workspaceId });

            throw err;
        }
    }

    if (state.type === "active" && state.actionType === "confirm_setup") {
        try {
            await handleStripeErrors(() => stripe.confirmCardSetup(state.setupIntent.client_secret, {
                payment_method: state.setupIntent.payment_method
            }));
        } catch (err) {
            logger.error(err, "[BillingController] upgradeWorkspacePlan() failed to confirm setup, will notify user");

            await ShowMessageDialog({
                title: "Warning",
                message: <>
                    There was an issue authorizing your payment method for future payments.
                    <br />
                    Your subscription was activated, but please update your payment method to make sure future payments are successful and your subscription is not interrupted.
                </>,
                buttonOptions: { color: "blue" }
            });
        }
    }

    if (billingController.initialized && billingController.workspaceId === workspaceId) {
        await billingController.reloadStripeData();
    }

    // Legacy
    if (options.tier === WorkspacePlanTier.PRO) {
        try {
            const productDefinition = stripeProducts[options.tier];
            const priceDefinition = productDefinition.plans.find(plan => "planId" in plan && plan.planId === options.priceId) as RecurringPlanDefinition;

            const { currentPlan } = await Api.trialDetail.get({ t: new Date().getTime() });
            const { slideCount } = await presentationsApi.getSlideCount({ workspaceId, filter: "all" });
            const params = new URLSearchParams(window.location.search);
            const testId = params.get("ex");
            const eventProps = {
                billing_term: priceDefinition.interval,
                current_status: currentPlan,
                amount: priceDefinition.amount / 100,
                plan: "pro",
                current_slide_count: slideCount,
                subscription_id: subscription.id,
                workspace_id: workspaceId,
                promo_code: options.promotionCode,
                price_id: options.priceId,
                plan_display_name: productDefinition.productName,
                test_id: testId,
                user_id: auth().currentUser.uid,
                ...(options.analyticsProps ?? {})
            };
            trackActivity("Upgrade", "BillingIntentComplete", testId, null, eventProps, { audit: true });
            await Api.klaviyoTrack.post({
                eventName: "Upgrade:BillingIntentComplete",
                eventProps,
            });

            trackActivity("SignupFlow", "NavForward", null, null, {
                step_number: 8,
                step_name: "Checkout",
                plan_selected: "pro",
                object: "button",
                object_label: "Start free trial",
                action: "clicked",
                workspace_id: "all"
            }, { audit: true });
        } catch (err) {
            logger.error(err, "[BillingController] upgradeWorkspacePlan() legacy analytics failed");
        }
    } else if (options.tier === WorkspacePlanTier.TEAM) {
        const productDefinition = stripeProducts[options.tier];
        const priceDefinition = productDefinition.plans.find(plan => "planId" in plan && plan.planId === options.priceId) as RecurringPlanDefinition;

        const params = new URLSearchParams(window.location.search);
        const testId = params.get("ex");

        const billingProps = {
            workspace_id: workspaceId,
            seats_added: options.quantity,
            type: "first_purchase",
            billing_term: priceDefinition.interval,
            total_seats: options.quantity,
            promo_code: options.promotionCode,
            price_id: options.priceId,
            plan_display_name: productDefinition.productName,
            test_id: testId,
            ...(options.analyticsProps ?? {})
        };
        trackActivity("Organization", "BillingIntentComplete", testId, null, billingProps, { audit: true });
        await Api.klaviyoTrack.post({
            eventName: "Organization:BillingIntentComplete",
            billingProps,
        });
    }
    // End Legacy
}
